import React, { useMemo, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import config from 'constants/config'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import setRegionCookie from 'cookies/setRegionCookie'
import { Status, setGeoRedirect } from 'storage/geo'

interface Props {
  checkoutCurrency: string
  mismatchedCurrencies: Array<string>
}

function CurrencyMismatchWarning({ checkoutCurrency, mismatchedCurrencies }: Props) {
  const intl = useIntl()

  const redirectRegions = useMemo(() => {
    return mismatchedCurrencies.reduce<Record<string, Array<App.RegionSummary>>>((acc, currency) => {
      acc[currency] = config.regions.filter(region => region.currencyCode === currency)
      return acc
    }, {})
  }, [mismatchedCurrencies])

  const onRegionLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 || e.button === 1) {
      const regionCode = e.currentTarget.dataset.regioncode ?? ''
      setRegionCookie(regionCode)
      setGeoRedirect(Status.redirected)
    }
  }, [])

  const heading = intl.formatMessage({
    id: 'QdkD4F35TP',
    defaultMessage: 'Currency mismatch',
    description: 'Section heading for currency mismatch warning banner',
  })

  return (
    <MessageBanner kind="warning" heading={heading}>
      <VerticalSpacer gap={8}>
        <div>
          <FormattedMessage
            id="MPdfAV0Wsk"
            defaultMessage="You have redeemed credit in {count, plural, one {a currency that does not} other {currencies that do not}} match the current checkout currency (<b>{checkoutCurrency}</b>)."
            description="Warning message when redeemed gift card credit currency does not match checkout currency"
            values={{
              count: mismatchedCurrencies.length,
              checkoutCurrency,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </div>
        {mismatchedCurrencies.map(currency => (<>
          <div>
            <FormattedMessage
              id="Gz/Ckt+RTI"
              defaultMessage="Redeemed credit for <b>{currency}</b> will be available in the following {regionCount, plural, one {region} other {regions}}:"
              description="Info message listing regions where redeemed credit can be used"
              values={{
                currency,
                regionCount: redirectRegions[currency].length,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </div>
          <ul>
            {redirectRegions[currency].map(region => (
              <li key={region.code}>
                <TextLink
                  href={`/${region.code.toLowerCase()}`}
                  data-regioncode={region.code}
                  onClick={onRegionLinkClick}
                  onAuxClick={onRegionLinkClick}
                >
                  {region.name}
                </TextLink>
              </li>
            ))}
          </ul>
        </>))}
      </VerticalSpacer>
    </MessageBanner>
  )
}

export default CurrencyMismatchWarning
